
import apiClient from './client'
import { 
  WorkflowRequest,
  WorkflowResponse,
  AnalysisRequest,
  AnalysisResponse,
  ThemeRequest,
  ThemeResponse,
  VerificationRequest,
  PromptTemplate,
  WorkflowPrompt
} from '../../types/workflow'
import { getClientIdFromUrl } from '../../utils/clientId'
import { debugLog } from '../../utils/debug'
import { snowflakeApi } from '../../utils/api';
import { versionedApiClient } from '../../api/versionedClient';
import { snowflakeService } from '../snowflakeService';

// Define interfaces for database types
export interface DatabaseClient {
  client_id: number;
  name: string;
  client_code: string;
  domains?: string;
  active: boolean;
  user_count?: number;
}

export interface DatabaseWorkflow {
  workflow_id: number;
  client_id: number;
  name: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  external_id?: string;
  created_by?: number;
  created_by_user?: {
    user_id: number;
    name: string;
    email: string;
    role?: string;
  };
  modules?: Array<{
    module_id: number;
    module_type?: string;
    type?: string;
    module_name?: string;
    name?: string;
    status: string;
    created_at: string;
    updated_at?: string;
    completed_at?: string | null;
  }>;
  module_counts?: Record<string, number>;
  total_modules?: number;
}

export interface DatabaseWorkflowResult {
  client_id: number;
  workflows: DatabaseWorkflow[];
  total: number;
  limit: number;
  offset: number;
}

export interface DatabaseModule {
  module_id: number;
  workflow_id: number;
  type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ModuleResponse {
  module_id: number;
  workflow_id?: string | number;
  status: string;
  message?: string;
  error?: string;
  [key: string]: any; // Allow for additional fields
}

// Helper function to track missing endpoints
const trackMissingEndpoint = (endpoint: string) => {
  // Log to Application Insights if available
  const appInsights = (window as any).__appInsights;
  if (appInsights) {
    appInsights.trackEvent({
      name: 'MissingDbEndpoint', 
      properties: {
        endpoint,
        timestamp: new Date().toISOString()
      }
    });
  }
};

// Create and export the workflow service object
export const workflowService = {
  // Track ongoing client creation requests to prevent duplicates
  _ongoingClientCreations: new Set<string>(),

  /**
   * Common error handling function for API errors
   * Logs to console and Application Insights, then throws the error
   */
  handleApiError(operation: string, id: string | number, error: any, extraInfo?: Record<string, any>): never {
    // Standard error logging
    console.error(`Error ${operation} for ${id}:`, error);
    
    // Application Insights tracking
    const appInsights = (window as any).__appInsights;
    if (appInsights) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(`Unknown error in ${operation}`),
        properties: {
          operation,
          id: String(id),
          errorType: error instanceof Error ? error.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
          ...extraInfo
        }
      });
    }
    
    throw error;
  },
  
  /**
   * Starts tracking an API call with Application Insights
   * @param operation - The operation name
   * @param id - The primary ID (workflow or module ID)
   * @param additionalProps - Additional properties to track
   * @returns Object containing correlation ID and start time
   */
  trackApiCallStart(operation: string, id: string | number, additionalProps: Record<string, any> = {}): { correlationId: string; startTime: number } {
    const startTime = Date.now();
    const correlationId = `${operation}-${id}-${startTime}`;
    
    const appInsights = (window as any).__appInsights;
    if (appInsights) {
      appInsights.trackEvent({
        name: `DbAPI_${operation}Started`,
        properties: {
          correlationId,
          id: String(id),
          operation,
          timestamp: new Date().toISOString(),
          ...additionalProps
        }
      });
    }
    
    return { correlationId, startTime };
  },

  /**
   * Tracks the successful completion of an API call
   * @param trackingInfo - The tracking info returned from trackApiCallStart
   * @param operation - The operation name
   * @param id - The primary ID (workflow or module ID)
   * @param responseData - Optional response data to include metrics from
   * @param additionalProps - Additional properties to track
   */
  trackApiCallSuccess(
    trackingInfo: { correlationId: string; startTime: number },
    operation: string,
    id: string | number,
    responseData?: any,
    additionalProps: Record<string, any> = {}
  ): void {
    const { correlationId, startTime } = trackingInfo;
    const duration = Date.now() - startTime;
    
    const appInsights = (window as any).__appInsights;
    if (appInsights) {
      // Extract some common metrics from response data if available
      const responseProps: Record<string, any> = {};
      if (responseData) {
        if (responseData.status) responseProps.responseStatus = responseData.status;
        if (responseData.module_id) responseProps.moduleId = responseData.module_id;
        if (responseData.results) responseProps.hasResults = true;
      }
      
      appInsights.trackEvent({
        name: `DbAPI_${operation}Completed`,
        properties: {
          correlationId,
          id: String(id),
          operation,
          durationMs: duration,
          timestamp: new Date().toISOString(),
          ...responseProps,
          ...additionalProps
        }
      });
    }
  },
  
  /**
   * Parse a client ID value ensuring it's in the correct format for API calls
   * This fixes issues with numeric client IDs being passed as strings
   * 
   * NOTE: This function handles client ID type conversion for DB API mode methods.
   * The ClientIdAudit warnings for DB methods can be ignored as parseClientId handles proper conversion.
   */
  parseClientId: (clientId: string | number): string | number => {
    // If it's already a number, return it
    if (typeof clientId === 'number') {
      return clientId;
    }
    
    // If it's a string that looks like a number, convert it
    if (typeof clientId === 'string' && /^\d+$/.test(clientId)) {
      return parseInt(clientId, 10);
    }
    
    // Otherwise return the original value (likely a client_code string)
    return clientId;
  },

  /**
   * Create a new workflow
   */
  createWorkflow: async (data: WorkflowRequest): Promise<WorkflowResponse> => {
    // Extract client_id from the request data
    // Make sure we cast the result properly to handle potential undefined values
    const rawClientId = data.client_id || getClientIdFromUrl() || '';
    
    // Parse the client ID to ensure correct type (string for client_code, number for numeric IDs)
    // Use the function directly instead of through the workflowService object to avoid self-reference
    const parseClientId = (clientId: string | number): string | number => {
      // If it's already a number, return it
      if (typeof clientId === 'number') {
        return clientId;
      }
      
      // If it's a string that looks like a number, convert it
      if (typeof clientId === 'string' && /^\d+$/.test(clientId)) {
        return parseInt(clientId, 10);
      }
      
      // Otherwise return the original value (likely a client_code string)
      return clientId;
    };
    
    const clientId = parseClientId(rawClientId);
    
    debugLog('workflowService', 'Creating workflow with client_id:', clientId);
    
    // Create a completely new request object WITHOUT client_id
    // This ensures client_id is never in the request body
    const cleanRequestData = {
      workflow_id: data.workflow_id,
      business_name: data.business_name,
      industry: data.industry || 'General',
      business_rules: data.business_rules || [],
      additional_context: data.additional_context || ''
    };
    

    
    debugLog('workflowService', 'Sending request with:', {
      body: cleanRequestData
    });
    
    try {
      // MIGRATION: Replace v1 endpoint with db endpoint
      // OLD: /api/v1/business-config
      // NEW: /api/v1/db/workflow
      const response = await apiClient.post<WorkflowResponse>(
        `/api/v1/db/client/${clientId}/workflow`, 
        cleanRequestData
      );
      
      debugLog('workflowService', 'Workflow creation response:', response.data);
      

      
      return response.data;
    } catch (error: any) {
      // Log detailed error info
      console.error('==== WORKFLOW CREATION ERROR ====');
      console.error('Request details:', {
        endpoint: '/api/v1/db/workflow',
        clientId: clientId,
        requestData: cleanRequestData
      });
      
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Error data:', error.response.data);
        console.error('Headers:', error.response.headers);
      } else {
        console.error('Error without response:', error.message);
      }
      console.error('==================================');
      
      // Log to Application Insights
      const appInsights = (window as any).__appInsights;
      if (appInsights) {
        appInsights.trackEvent({
          name: 'WorkflowCreationError',
          properties: {
            clientId: clientId,
            requestData: JSON.stringify(cleanRequestData),
            errorMessage: error.response?.data?.detail || error.message,
            statusCode: error.response?.status
          }
        });
      }
      
      throw error;
    }
  },

  /**
   * Start keyword analysis
   */
  startAnalysis: async (data: AnalysisRequest, clientId?: string): Promise<AnalysisResponse> => {
    // Use provided clientId or get from URL/localStorage
    const effectiveClientId = clientId || getClientIdFromUrl();
    
    // Extract client_id from request body if present (to avoid duplication)
    const { client_id, workflow_id, ...requestFields } = data as any;
    
    debugLog('workflowService', 'Starting analysis with workflow_id:', workflow_id);
    
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/analyze with workflow_id in body
    // NEW: /api/v1/db/workflow/{workflow_id}/analyze
    const response = await apiClient.post<AnalysisResponse>(
      `/api/v1/db/workflow/${workflow_id}/analyze`, 
      requestFields
    );
    
    return response.data;
  },

  /**
   * Get analysis status
   */
  getAnalysisStatus: async (workflowId: string, clientId?: string): Promise<AnalysisResponse> => {
    // Use provided clientId or get from URL/localStorage
    const effectiveClientId = clientId || getClientIdFromUrl();
    
    debugLog('workflowService', 'Getting analysis status for workflow:', workflowId);
    
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/analyze/{workflow_id} with client_id as query param
    // NEW: /api/v1/db/workflow/{workflow_id}/analyze
    const response = await apiClient.get<AnalysisResponse>(
      `/api/v1/db/workflow/${workflowId}/analyze`
    );
    
    return response.data;
  },

  /**
   * Add custom themes
   */
  addCustomThemes: async (data: ThemeRequest, clientId?: string): Promise<ThemeResponse> => {
    // Extract workflow_id from request data
    const { workflow_id, ...requestFields } = data;
    
    debugLog('workflowService', 'Adding custom themes for workflow:', workflow_id);
    
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/themes with workflow_id in body and client_id as query param
    // NEW: /api/v1/db/workflow/{workflow_id}/themes
    const response = await apiClient.post<ThemeResponse>(
      `/api/v1/db/workflow/${workflow_id}/themes`, 
      requestFields
    );
    
    return response.data;
  },

  /**
   * Verify analysis results
   */
  verifyAnalysis: async (data: VerificationRequest, clientId?: string): Promise<AnalysisResponse> => {
    // Extract workflow_id from the request
    const { workflow_id, ...requestFields } = data;
    
    debugLog('workflowService', 'Verifying analysis for workflow:', workflow_id);
    
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/verify with workflow_id in body and client_id as query param
    // NEW: /api/v1/db/workflow/{workflow_id}/verify
    const response = await apiClient.post<AnalysisResponse>(
      `/api/v1/db/workflow/${workflow_id}/verify`, 
      requestFields
    );
    
    return response.data;
  },

  /**
   * Get prompt templates
   */
  getPromptTemplates: async (): Promise<PromptTemplate[]> => {
    const response = await apiClient.get<PromptTemplate[]>('/api/v1/prompts');
    return response.data;
  },

  /**
   * Create prompt template
   */
  createPromptTemplate: async (data: PromptTemplate): Promise<PromptTemplate> => {
    const response = await apiClient.post<PromptTemplate>('/api/v1/prompts', data);
    return response.data;
  },

  /**
   * Update workflow prompt
   */
  updateWorkflowPrompt: async (
    workflowId: string, 
    data: WorkflowPrompt,
    clientId?: string
  ): Promise<WorkflowPrompt> => {
    // Use provided clientId or get from URL/localStorage
    const effectiveClientId = clientId || getClientIdFromUrl();
    
    // Add client_id as query parameter
    const config = effectiveClientId ? {
      params: { client_id: effectiveClientId }
    } : undefined;
    
    debugLog('workflowService', 'Updating workflow prompt with client_id:', effectiveClientId);
    
    const response = await apiClient.put<WorkflowPrompt>(
      `/api/v1/workflows/${workflowId}/prompt`,
      data,
      config
    );
    
    return response.data;
  },

  /**
   * Get workflow results
   */
  getWorkflowResults: async (workflowId: string, clientId?: string): Promise<any> => {
    // Use provided clientId or get from URL/localStorage
    const effectiveClientId = clientId || getClientIdFromUrl();
    
    // Add client_id as query parameter
    const config = effectiveClientId ? {
      params: { client_id: effectiveClientId }
    } : undefined;
    
    debugLog('workflowService', 'Getting workflow results with client_id:', effectiveClientId);
    
    const response = await apiClient.get(
      `/api/v1/workflows/${workflowId}/results`,
      config
    );
    
    return response.data;
  },

  /**
   * Export workflow results
   */
  exportResults: async (workflowId: string, format: 'csv' | 'json' = 'csv', clientId?: string): Promise<string> => {
    // Use provided clientId or get from URL/localStorage
    const effectiveClientId = clientId || getClientIdFromUrl();
    
    // Build params with format and client_id
    const params: Record<string, string> = { format };
    if (effectiveClientId) {
      params.client_id = effectiveClientId;
    }
    
    debugLog('workflowService', 'Exporting results with client_id:', effectiveClientId);
    
    const response = await apiClient.get(
      `/api/v1/workflows/${workflowId}/export`,
      { params }
    );
    
    return response.data.download_url;
  },

  /**
   * Get workflow by ID
   */
  async getWorkflow(workflowId: number | string, clientId?: number): Promise<any> {
    try {
      debugLog('workflowService', `Getting workflow ${workflowId}`);
      
      // Log all API calls for debugging
      const appInsights = (window as any).__appInsights;
      const correlationId = `workflow-fetch-${Date.now()}`;
      if (appInsights) {
        appInsights.trackEvent({
          name: 'WorkflowFetchAttempt',
          properties: {
            correlationId,
            workflowId: String(workflowId),
            clientId: clientId ? String(clientId) : 'not_provided',
            idType: typeof workflowId === 'string' && workflowId.includes('-') ? 'uuid' : 'numeric',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // First, check if we have a cached mapping in localStorage for this ID
      if (typeof workflowId === 'string' && workflowId.includes('-')) {
        const cachedDbId = localStorage.getItem(`external_id_${workflowId}`);
        
        // Only use cached ID if it's a positive number (valid database ID)
        if (cachedDbId && /^[1-9]\d*$/.test(cachedDbId)) {
          console.log(`Found cached mapping: external ID ${workflowId} -> DB ID ${cachedDbId}`);
          
          try {
            // Try to fetch using the cached database ID
            const response = await snowflakeApi.get(`/api/v1/db/workflow/${cachedDbId}`);
            
            if (response.data) {
              console.log(`Successfully fetched workflow using cached database ID ${cachedDbId}`);
              
              // Log successful lookup
              if (appInsights) {
                appInsights.trackEvent({
                  name: 'CachedIdLookupSuccess',
                  properties: {
                    correlationId,
                    externalId: workflowId,
                    databaseId: cachedDbId,
                    timestamp: new Date().toISOString()
                  }
                });
              }
              
              return response.data;
            }
          } catch (cachedIdError) {
            console.error(`Error using cached database ID ${cachedDbId}:`, cachedIdError);
            // Clear invalid cache entry
            localStorage.removeItem(`external_id_${workflowId}`);
            console.log(`Removed invalid cached mapping for ${workflowId}`);
            // Don't throw yet - we'll try external ID endpoint next
          }
        } else if (cachedDbId) {
          // We have a cached ID but it's invalid (like -1)
          console.log(`Found invalid cached mapping: ${cachedDbId} for ${workflowId}, removing it`);
          localStorage.removeItem(`external_id_${workflowId}`);
        }
      }
      
      // Next check if the ID is a UUID (likely an external ID)
      if (typeof workflowId === 'string' && workflowId.includes('-')) {
        // This looks like a UUID/external ID, use the external endpoint directly
        console.log(`Using external ID endpoint for: ${workflowId}`);
        try {
          const response = await snowflakeApi.get(`/api/v1/db/workflow/external/${workflowId}`);
          
          // For telemetry purposes
          if (response.data && response.data.workflow_id) {
            // Store the mapping in localStorage for future use
            localStorage.setItem(`external_id_${workflowId}`, response.data.workflow_id.toString());
            localStorage.setItem(`workflow_${response.data.workflow_id}_external_id`, String(workflowId));
            
            // Log usage of external ID for future reference
            if (appInsights) {
              appInsights.trackEvent({
                name: 'ExternalIdLookupSuccess',
                properties: {
                  correlationId,
                  external_id: workflowId,
                  database_id: response.data.workflow_id,
                  timestamp: new Date().toISOString()
                }
              });
            }
          }
          
          return response.data;
        } catch (externalIdError) {
          console.error(`Error using external ID endpoint for ${workflowId}:`, externalIdError);
          
          // Log the failed external ID lookup
          if (appInsights) {
            appInsights.trackException({
              exception: externalIdError instanceof Error ? externalIdError : new Error('External ID lookup failed'),
              properties: {
                correlationId,
                externalId: workflowId,
                operation: 'getWorkflow',
                endpoint: `/api/v1/db/workflow/external/${workflowId}`,
                errorMessage: externalIdError instanceof Error ? externalIdError.message : String(externalIdError),
                timestamp: new Date().toISOString()
              }
            });
          }
          
          // Try looking for a workflow with this external ID in the client's workflows
          if (clientId) {
            try {
              console.log(`Trying to find workflow with external ID ${workflowId} in client ${clientId}'s workflows`);
              const clientWorkflows = await this.getDbClientWorkflows(clientId);
              
              if (clientWorkflows && clientWorkflows.workflows && clientWorkflows.workflows.length > 0) {
                const matchingWorkflow = clientWorkflows.workflows.find(w => w.external_id === workflowId);
                
                if (matchingWorkflow) {
                  console.log(`Found workflow with external ID ${workflowId} in client ${clientId}'s workflows:`, matchingWorkflow);
                  
                  // Store the mapping for future use
                  localStorage.setItem(`external_id_${workflowId}`, matchingWorkflow.workflow_id.toString());
                  localStorage.setItem(`workflow_${matchingWorkflow.workflow_id}_external_id`, workflowId);
                  
                  // Log success
                  if (appInsights) {
                    appInsights.trackEvent({
                      name: 'ExternalIdFoundInClientWorkflows',
                      properties: {
                        correlationId,
                        externalId: workflowId,
                        clientId: String(clientId),
                        databaseId: matchingWorkflow.workflow_id,
                        timestamp: new Date().toISOString()
                      }
                    });
                  }
                  
                  return matchingWorkflow;
                }
              }
            } catch (clientWorkflowsError) {
              console.error(`Error trying to find workflow in client ${clientId}'s workflows:`, clientWorkflowsError);
              // Don't throw yet - we'll try numeric ID approach as fallback
            }
          }
          
          // Don't throw yet - we'll try numeric ID approach as fallback
        }
      }
      
      // If it's not clearly a UUID or the external endpoint failed, try numeric ID approach
      // Convert string ID to number if possible for consistent handling
      const numericId = this.parseId(workflowId);
      
      if (numericId) {
        // If it's a valid numeric ID, use the standard database endpoint
        try {
          console.log(`Using numeric ID endpoint for: ${numericId}`);
          const response = await snowflakeApi.get(`/api/v1/db/workflow/${numericId}`);
          
          // Log successful numeric ID lookup
          if (appInsights) {
            appInsights.trackEvent({
              name: 'NumericIdLookupSuccess',
              properties: {
                correlationId,
                numericId: String(numericId),
                timestamp: new Date().toISOString()
              }
            });
          }
          
          return response.data;
        } catch (numericIdError) {
          console.error(`Error using numeric ID endpoint for ${numericId}:`, numericIdError);
          
          // Log the failed numeric ID lookup
          if (appInsights) {
            appInsights.trackException({
              exception: numericIdError instanceof Error ? numericIdError : new Error('Numeric ID lookup failed'),
              properties: {
                correlationId,
                numericId: String(numericId),
                operation: 'getWorkflow',
                endpoint: `/api/v1/db/workflow/${numericId}`,
                errorMessage: numericIdError instanceof Error ? numericIdError.message : String(numericIdError),
                timestamp: new Date().toISOString()
              }
            });
          }
          
          // Throw the error if we've exhausted all options
          throw numericIdError;
        }
      } 
      
      // As a last resort for backwards compatibility - try the external ID endpoint again
      // This is for IDs that aren't clearly UUIDs or numeric
      console.log(`Fallback to external ID lookup for: ${workflowId}`);
      try {
        const response = await snowflakeApi.get(`/api/v1/db/workflow/external/${workflowId}`);
        
        // For telemetry purposes
        if (response.data && response.data.workflow_id) {
          // Store the mapping for future use
          localStorage.setItem(`external_id_${workflowId}`, response.data.workflow_id.toString());
          localStorage.setItem(`workflow_${response.data.workflow_id}_external_id`, workflowId.toString());
          
          // Log usage of external ID for future reference
          if (appInsights) {
            appInsights.trackEvent({
              name: 'FallbackExternalIdSuccess',
              properties: {
                correlationId,
                external_id: workflowId,
                database_id: response.data.workflow_id,
                timestamp: new Date().toISOString()
              }
            });
          }
        }
        
        return response.data;
      } catch (fallbackError) {
        console.error(`Final fallback to external ID lookup failed for ${workflowId}:`, fallbackError);
        
        // Log the failed fallback attempt
        if (appInsights) {
          appInsights.trackException({
            exception: fallbackError instanceof Error ? fallbackError : new Error('Fallback external ID lookup failed'),
            properties: {
              correlationId,
              workflowId: String(workflowId),
              operation: 'getWorkflow',
              endpoint: `/api/v1/db/workflow/external/${workflowId}`,
              errorMessage: fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
              timestamp: new Date().toISOString()
            }
          });
        }
        
        // Create a placeholder workflow if all lookups fail
        console.log('All lookup methods failed, creating placeholder workflow');
        
        // Get current time for timestamps
        const now = new Date().toISOString();
        
        // Return a placeholder workflow
        const placeholderWorkflow = {
          workflow_id: -1, // Negative ID indicates placeholder
          external_id: typeof workflowId === 'string' ? workflowId : String(workflowId),
          name: "Workflow not found",
          description: "This workflow could not be found in the database. It may have been deleted or the ID is incorrect.",
          status: "error", // Custom status to indicate lookup failure
          created_at: now,
          updated_at: now,
          is_active: false,
          client: clientId ? { client_id: clientId, name: `Client ${clientId}` } : null,
          modules: [],
          module_counts: {},
          total_modules: 0,
          _placeholder: true, // Special flag to indicate this is a placeholder
          _error: true // Flag to indicate this is an error state
        };
        
        // Log creation of placeholder
        if (appInsights) {
          appInsights.trackEvent({
            name: 'PlaceholderWorkflowCreated',
            properties: {
              correlationId,
              workflowId: String(workflowId),
              timestamp: new Date().toISOString()
            }
          });
        }
        
        return placeholderWorkflow;
      }
    } catch (error) {
      console.error(`Error fetching workflow ${workflowId}:`, error);
      
      // Log the fatal error
      const appInsights = (window as any).__appInsights;
      if (appInsights) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Failed to fetch workflow'),
          properties: {
            workflowId: String(workflowId),
            clientId: clientId ? String(clientId) : 'not_provided',
            operation: 'getWorkflow',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  },

  // DATABASE-FIRST METHODS

  // Helper method to get the appropriate API client based on module type
  // All modules now use Snowflake BFF
  getApiClient(moduleType: string) {
    return snowflakeApi;
  },

     // Create a new client in the database
   async createDbClient(name: string, clientCode: string, isActive: boolean = true, industry?: string, domains?: string): Promise<DatabaseClient> {
     // Create a unique key for this client creation request
     const requestKey = `${clientCode}-${name}`;
     
     // Check for duplicate requests
     if (this._ongoingClientCreations.has(requestKey)) {
       console.warn(`Duplicate client creation request detected for: ${requestKey}`);
       throw new Error(`Client creation already in progress for: ${clientCode}`);
     }
     
     // Mark this request as ongoing
     this._ongoingClientCreations.add(requestKey);
    
              try {
       const requestData: any = {
         name,
         client_code: clientCode,
         active: isActive
       };
       
       // Only include industry if provided
       if (industry) {
         requestData.industry = industry;
       }
       
       // Only include domains if provided (allow empty string)
       if (domains !== undefined && domains !== null) {
         requestData.domains = domains;
       }
       
       console.log('Creating client:', { name, clientCode, industry, domains: domains ? 'provided' : 'none' });
       const response = await snowflakeApi.post('/api/v1/db/client', requestData);
       
       if (!response.data || !response.data.client_id) {
         console.error('Invalid response from server:', response.data);
         throw new Error('Failed to create client: Invalid response from server');
       }
       
       console.log('Successfully created client:', { 
         client_id: response.data.client_id, 
         client_code: response.data.client_code,
         name: response.data.name 
       });
      
                    // Sync client to Snowflake after successful creation
       try {
         const snowflakeResponse = await snowflakeService.syncClient({
           client_id: response.data.client_id,
           client_code: response.data.client_code,
           name: response.data.name,
           industry: response.data.industry,
           domains: response.data.domains,
           is_active: response.data.active // Note: main DB uses 'active', Snowflake uses 'is_active'
         });
         
         if (!snowflakeResponse.success) {
           console.error('Snowflake sync failed:', snowflakeResponse.error);
         }
        
                 // Log success to Application Insights if available (only if sync was successful)
         if (snowflakeResponse.success) {
           const appInsights = (window as any).__appInsights;
           if (appInsights) {
             appInsights.trackEvent({
               name: 'ClientSyncedToSnowflake',
               properties: {
                 operation: 'createDbClient',
                 clientId: response.data.client_id,
                 clientCode: response.data.client_code,
                 clientName: response.data.name,
                 timestamp: new Date().toISOString()
               }
             });
           }
         }
        
             } catch (snowflakeError) {
         console.error('Failed to sync client to Snowflake (non-blocking error):', snowflakeError);
         
         // Log to Application Insights if available
         const appInsights = (window as any).__appInsights;
         if (appInsights) {
           appInsights.trackException({
             exception: snowflakeError instanceof Error ? snowflakeError : new Error('Snowflake sync failed'),
             properties: {
               operation: 'createDbClient',
               phase: 'snowflakeSync',
               clientId: response.data.client_id,
               clientCode: response.data.client_code,
               clientName: response.data.name,
               timestamp: new Date().toISOString()
             }
           });
         }
         
         // Don't throw here - Snowflake sync failure should not block client creation
         console.log('Client creation succeeded despite Snowflake sync failure');
       }
      return response.data;
         } catch (error) {
       console.error('Error creating database client:', error);
       
       // Log to Application Insights if available
       const appInsights = (window as any).__appInsights;
       if (appInsights) {
         appInsights.trackException({
           exception: error instanceof Error ? error : new Error('Unknown error creating client'),
           properties: {
             operation: 'createDbClient',
             endpoint: '/api/v1/db/client',
             name,
             clientCode,
             industry,
             httpStatus: (error as any)?.response?.status || 'unknown',
             timestamp: new Date().toISOString()
           }
         });
       }
       
       throw error;
    } finally {
      // Always cleanup request tracking
      this._ongoingClientCreations.delete(requestKey);
    }
  },

  // Get all clients (unpaginated - for backward compatibility)
  async getDbClients(): Promise<DatabaseClient[]> {
    try {
      debugLog('workflowService', 'Fetching all database clients');
      
      // Direct access URL to ensure we're going to the correct endpoint
      const directUrl = `/api/v1/db/clients`;
      
      console.log('Attempting to fetch DB clients from:', directUrl);
      
      // Use axios directly with apiClient to avoid interceptors that might add client_id
      // Fetch all clients with a high limit
      const response = await snowflakeApi.request({
        url: directUrl,
        method: 'GET',
        params: {
          limit: 1000,
          offset: 0,
          active_only: true
        },
        // Set validateStatus to handle 404 gracefully
        validateStatus: function (status) {
          return status < 500; // Resolve only if the status code is less than 500
        }
      });
      
      console.log('DB clients response:', response.status, response.headers?.['content-type']);
      
      if (response.status === 404) {
        console.warn('Database clients endpoint not found. This might be a configuration issue.');
        trackMissingEndpoint('/api/v1/db/clients');
        // Log to Application Insights if available
        const appInsights = (window as any).__appInsights;
        if (appInsights) {
          appInsights.trackEvent({
            name: 'MissingDbEndpoint', 
            properties: {
              endpoint: '/api/v1/db/clients',
              timestamp: new Date().toISOString()
            }
          });
        }
        return [];
      }        
   
      // Check if response.data is an array or contains a clients property
      // This handles different API response formats
      if (Array.isArray(response.data)) {
        debugLog('workflowService', `Successfully fetched ${response.data.length} clients from database`);
        return response.data;
      }
      
      // Check if response.data is an object that contains an array of clients
      if (response.data && typeof response.data === 'object') {
        // First check if there is a clients property that contains an array
        if (response.data.clients && Array.isArray(response.data.clients)) {
          debugLog('workflowService', `Successfully fetched ${response.data.clients.length} clients from database (found in clients property)`);
          return response.data.clients;
        }
        
        // If no clients property is found, continue looking for any array property
        // Log what we've actually received to help with debugging
        console.log('Response data structure:', Object.keys(response.data));
        
        // Look for array properties in the response that might contain clients
        for (const key in response.data) {
          if (Array.isArray(response.data[key])) {
            const possibleClients = response.data[key];
            if (possibleClients.length > 0 && possibleClients[0].client_id !== undefined) {
              debugLog('workflowService', `Successfully fetched ${possibleClients.length} clients from database (found in '${key}' property)`);
              return possibleClients;
            }
          }
        }
        
        // Special case: If there's a "clients" property but it's not an array, log it specifically
        if ('clients' in response.data && !Array.isArray(response.data.clients)) {
          console.warn('Found "clients" property but it is not an array:', typeof response.data.clients);
        }
      }
      
      // If we got here, we couldn't find an array of clients in the response
      console.warn('API returned unexpected data structure for clients. Expected array of clients but got:', 
        response.data === undefined ? 'undefined' : typeof response.data);
      
      // Return empty array as fallback to prevent UI crashes
      return [];
    } catch (error) {
      console.error('Error fetching database clients:', error);
      trackMissingEndpoint('/api/v1/db/clients');
      
      // Log to Application Insights if available
      const appInsights = (window as any).__appInsights;
      if (appInsights) {
        appInsights.trackException({
          exception: error as Error,
          properties: {
            operation: 'getDbClients',
            endpoint: '/api/v1/db/clients',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Return empty array instead of throwing error to prevent UI crashes
      return [];
    }
  },

  // Get clients with pagination support
  async getDbClientsPaginated(options: { limit?: number; offset?: number; active_only?: boolean; search?: string; process_status?: string; sort_by?: string; sort_order?: string } = {}): Promise<{ clients: DatabaseClient[]; total: number }> {
    try {
      const { limit = 20, offset = 0, active_only = true, search, process_status, sort_by = 'name', sort_order = 'asc' } = options;
      debugLog('workflowService', `Fetching paginated database clients (limit=${limit}, offset=${offset}, active_only=${active_only}, search=${search}, process_status=${process_status}, sort_by=${sort_by}, sort_order=${sort_order})`);
      
      // Direct access URL to ensure we're going to the correct endpoint
      const directUrl = `/api/v1/db/clients`;
      
      // Build params object, only include optional params if they're provided
      const params: any = {
        limit,
        offset,
        active_only,
        sort_by,
        sort_order
      };
      
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      if (process_status && process_status !== 'all') {
        params.process_status = process_status;
      }
      
      // Use axios directly with apiClient to avoid interceptors that might add client_id
      const response = await snowflakeApi.request({
        url: directUrl,
        method: 'GET',
        // Pass pagination parameters
        params,
        // Set validateStatus to handle 404 gracefully
        validateStatus: function (status) {
          return status < 500; // Resolve only if the status code is less than 500
        }
      });
      
      console.log('Paginated DB clients response:', response.status, response.headers?.['content-type']);
      
      if (response.status === 404) {
        console.warn('Database clients endpoint not found. This might be a configuration issue.');
        trackMissingEndpoint('/api/v1/db/clients');
        return { clients: [], total: 0 };
      }        
   
      // Check if response.data is an array or contains a clients property
      if (Array.isArray(response.data)) {
        debugLog('workflowService', `Successfully fetched ${response.data.length} paginated clients from database`);
        return { clients: response.data, total: response.data.length };
      }
      
      // Check if response.data is an object that contains an array of clients
      if (response.data && typeof response.data === 'object') {
        // First check if there is a clients property that contains an array
        if (response.data.clients && Array.isArray(response.data.clients)) {
          const total = response.data.total || response.data.clients.length;
          debugLog('workflowService', `Successfully fetched ${response.data.clients.length} paginated clients from database (total: ${total})`);
          return { clients: response.data.clients, total };
        }
      }
      
      // If we got here, we couldn't find an array of clients in the response
      console.warn('API returned unexpected data structure for paginated clients');
      
      // Return empty result as fallback to prevent UI crashes
      return { clients: [], total: 0 };
    } catch (error) {
      console.error('Error fetching paginated database clients:', error);
      trackMissingEndpoint('/api/v1/db/clients');
      
      // Return empty result instead of throwing error to prevent UI crashes
      return { clients: [], total: 0 };
    }
  },

  // Get all workflows for a client
  async getDbClientWorkflows(
    clientId: number, 
    options: { status?: string; limit?: number; offset?: number } = {}
  ): Promise<DatabaseWorkflowResult> {
    try {
      const { status, limit = 100, offset = 0 } = options;
      const params: Record<string, any> = { limit, offset };
      
      if (status) {
        params.status = status;
      }
      
      const response = await snowflakeApi.get(`/api/v2/db/client/${clientId}/workflows`, { 
        params,
        // Set validateStatus to handle 404 gracefully
        validateStatus: function (status) {
          return status < 500; // Resolve only if the status code is less than 500
        }
      });
      
      if (response.status === 404) {
        console.warn(`Workflows endpoint not found for client ${clientId}. This might be a configuration issue.`);
        trackMissingEndpoint(`/api/v2/db/client/${clientId}/workflows`);
        // Return empty result to prevent UI crashes
        return {
          client_id: clientId,
          workflows: [],
          total: 0,
          limit,
          offset
        };
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching workflows for client ${clientId}:`, error);
      trackMissingEndpoint(`/api/v2/db/client/${clientId}/workflows`);
      
      // Log to Application Insights if available
      const appInsights = (window as any).__appInsights;
      if (appInsights) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error(`Error fetching workflows for client ${clientId}`),
          properties: {
            operation: 'getDbClientWorkflows',
            clientId,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Return empty result instead of throwing to prevent UI crashes
      return {
        client_id: clientId,
        workflows: [],
        total: 0,
        limit: options.limit || 100,
        offset: options.offset || 0
      };
    }
  },

  // Get a specific workflow by database ID
  async getDbWorkflow(workflowId: number | string): Promise<DatabaseWorkflow> {
    try {
      console.log(`Fetching workflow with ID: ${workflowId}`);
      
      // First determine if this is a numeric ID or a UUID
      if (typeof workflowId === 'string' && workflowId.includes('-')) {
        // This looks like a UUID/external ID, so use the external endpoint instead
        console.log(`ID appears to be a UUID, using external ID endpoint`);
        return this.getDbWorkflowByExternal(workflowId);
      }
      
      // Use the new Workflow V2 endpoint that directly queries the database
      // This is optimized and includes all modules in one call
      const response = await snowflakeApi.get(`/api/v1/workflow-v2/workflow/${workflowId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching workflow ${workflowId}:`, error);
      throw error;
    }
  },
  
  // Get a specific workflow by external ID - for backwards compatibility
  async getDbWorkflowByExternal(externalId: string): Promise<DatabaseWorkflow> {
    try {
      console.log(`Fetching workflow with external ID: ${externalId}`);
      
      // First check if we have this ID mapped in localStorage
      const cachedDbId = localStorage.getItem(`external_id_${externalId}`);
      if (cachedDbId && cachedDbId !== '-1' && cachedDbId !== 'placeholder') {
        console.log(`Found cached database ID ${cachedDbId} for external ID ${externalId}`);
        
        try {
          // Try to fetch the workflow with this database ID
          const response = await snowflakeApi.get(`/api/v1/db/workflow/${cachedDbId}`);
          
          if (response.data) {
            console.log(`Successfully fetched workflow using cached database ID ${cachedDbId}`);
            return response.data;
          }
        } catch (cachedIdError) {
          console.error(`Error fetching workflow with cached ID ${cachedDbId}:`, cachedIdError);
          // Continue to external ID endpoint if this fails
        }
      }
      
      // Detailed endpoint logging for debugging
      console.log(`Making API request to: /api/v1/db/workflow/external/${externalId}`);
      
      // Log all available debug info to help detect issues
      const appInsights = (window as any).__appInsights;
      if (appInsights) {
        appInsights.trackEvent({
          name: 'ExternalIdLookupAttempt', 
          properties: {
            externalId,
            endpoint: `/api/v1/db/workflow/external/${externalId}`,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Set longer timeout for this request
      try {
        const response = await snowflakeApi.get(`/api/v1/db/workflow/external/${externalId}`, {
          timeout: 10000 // 10 second timeout to allow for network delays
        });
        
        // Log success telemetry
        if (appInsights && response.data) {
          appInsights.trackEvent({
            name: 'ExternalIdLookupSuccess', 
            properties: {
              externalId,
              databaseId: response.data.workflow_id,
              timestamp: new Date().toISOString()
            }
          });
        }
        
        // Store this mapping in localStorage for future reference
        if (response.data && response.data.workflow_id) {
          localStorage.setItem(`external_id_${externalId}`, response.data.workflow_id.toString());
          localStorage.setItem(`workflow_${response.data.workflow_id}_external_id`, externalId);
        }
        
        console.log(`Successfully retrieved workflow for external ID ${externalId}:`, response.data);
        return response.data;
      } catch (externalIdError: any) {
        console.error(`Error fetching workflow with external ID endpoint for ${externalId}:`, externalIdError);
        
        // Enhanced error logging
        if (externalIdError.response) {
          console.error('Response status:', externalIdError.response.status);
          console.error('Response headers:', externalIdError.response.headers);
          console.error('Response data:', externalIdError.response.data);
        }
        
        // Try to find this workflow ID in all client workflows as a last resort
        try {
          console.log('Trying to find workflow in client workflows as fallback');
          const result = await this.getDbClientsPaginated({ limit: 5, offset: 0 });
          
          // Use the first 5 clients to avoid performance issues
          const topClients = result.clients;
          
          for (const client of topClients) {
            console.log(`Checking workflows for client ${client.client_id} (${client.client_code})`);
            const clientWorkflows = await this.getDbClientWorkflows(client.client_id);
            
            if (clientWorkflows && clientWorkflows.workflows && clientWorkflows.workflows.length > 0) {
              const matchingWorkflow = clientWorkflows.workflows.find(w => w.external_id === externalId);
              
              if (matchingWorkflow) {
                console.log(`Found workflow with external ID ${externalId} in client ${client.client_id}'s workflows:`, matchingWorkflow);
                
                // Store the mapping for future use
                localStorage.setItem(`external_id_${externalId}`, matchingWorkflow.workflow_id.toString());
                localStorage.setItem(`workflow_${matchingWorkflow.workflow_id}_external_id`, externalId);
                
                // Log success
                if (appInsights) {
                  appInsights.trackEvent({
                    name: 'ExternalIdFoundInClientWorkflows',
                    properties: {
                      externalId: externalId,
                      clientId: String(client.client_id),
                      databaseId: matchingWorkflow.workflow_id,
                      timestamp: new Date().toISOString()
                    }
                  });
                }
                
                return matchingWorkflow;
              }
            }
          }
        } catch (clientWorkflowsError) {
          console.error('Error searching client workflows:', clientWorkflowsError);
        }
        
        // Create a placeholder workflow as a last resort
        console.log('Creating placeholder workflow for external ID:', externalId);
        
        const now = new Date().toISOString();
        const placeholderWorkflow = {
          workflow_id: -1,
          external_id: externalId,
          name: "Workflow loading...",
          description: "This workflow is being synchronized. Please refresh in a few moments.",
          status: "syncing",
          created_at: now,
          updated_at: now,
          is_active: true,
          client_id: null,
          modules: [],
          _placeholder: true
        };
        
        // Log placeholder creation
        if (appInsights) {
          appInsights.trackEvent({
            name: 'PlaceholderWorkflowCreated', 
            properties: {
              externalId,
              reason: 'External ID not found in database',
              timestamp: new Date().toISOString()
            }
          });
        }
        
        return placeholderWorkflow as any;
      }
    } catch (error: any) {
      console.error(`Error in getDbWorkflowByExternal for ${externalId}:`, error);
      
      // Log failure to Application Insights
      const appInsights = (window as any).__appInsights;
      if (appInsights) {
        appInsights.trackException({
          exception: error,
          properties: {
            externalId,
            operation: 'getDbWorkflowByExternal',
            errorMessage: error.message,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Create a placeholder workflow for any unexpected errors
      const now = new Date().toISOString();
      const placeholderWorkflow = {
        workflow_id: -1,
        external_id: externalId,
        name: "Error loading workflow",
        description: "There was an error loading this workflow. Please try refreshing the page.",
        status: "error",
        created_at: now,
        updated_at: now,
        is_active: false,
        client_id: null,
        modules: [],
        _placeholder: true,
        _error: true
      };
      
      return placeholderWorkflow as any;
    }
  },

  // Get modules for a workflow
  // @deprecated - Use getDbWorkflow() instead, which includes modules in response
  // This endpoint is no longer used in the codebase as of 2025-11-21
  async getDbWorkflowModules(workflowId: number, moduleType?: string): Promise<DatabaseModule[]> {
    try {
      let params = {};
      if (moduleType) {
        params = { type: moduleType };
      }
      
      const response = await snowflakeApi.get(`/api/v1/workflow-v2/workflow/${workflowId}/modules`, { params });
      return response.data.modules;
    } catch (error) {
      console.error(`Error fetching modules for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Mark file upload as completed
  async completeFileUpload(workflowId: number): Promise<any> {
    try {
      const response = await snowflakeApi.post(`/api/v1/workflow-v2/file-upload/complete?workflow_id=${workflowId}`);
      return response.data;
    } catch (error) {
      console.error(`Error completing file upload for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Get a specific module
  async getDbModule(moduleId: number, moduleType: string): Promise<DatabaseModule> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching module ${moduleId}:`, error);
      throw error;
    }
  },

  // Get module results
  async getDbModuleResults(moduleId: number, moduleType: string): Promise<any> {
    try {
      const apiClient = this.getApiClient(moduleType);
      const response = await apiClient.get(`/api/v1/db/module/${moduleId}/results`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching results for module ${moduleId}:`, error);
      throw error;
    }
  },

  // Create a new workflow
  async createDbWorkflow(clientId: number, name: string, description?: string, externalId?: string): Promise<DatabaseWorkflow> {
    try {
      // Use our tracking helper to get correlationId and start time
      const tracking = this.trackApiCallStart('WorkflowCreation', clientId, {
        name,
        description: description || '',
        externalId: externalId || ''
      });
      
      const { correlationId } = tracking;
      

      
      // Prepare request payload - do not include external_id as backend API doesn't support it
      const requestPayload = {
        name,
        description
        // external_id parameter removed - backend API doesn't accept it
        // Error: WorkflowService.create_workflow() got an unexpected keyword argument 'external_id'
      };

      // Log if externalId was provided but ignored
      if (externalId) {
        console.warn('Note: external_id parameter provided but not passed to API as the backend does not support it');
      }
      
      // Timeout and retry configuration
      const maxRetries = 2;
      const timeout = 15000; // 15 seconds timeout
      
      let retryCount = 0;
      let lastError = null;
      
      // Retry loop for transient failures
      while (retryCount <= maxRetries) {
        try {
          const startTime = Date.now();
          
          // Set timeout for the request - using v2 API endpoint
          const response = await snowflakeApi.post(
            `/api/v2/db/client/${clientId}/workflows`, 
            requestPayload,
            { timeout }
          );
          
          const requestDuration = Date.now() - startTime;
          
          // Validate response
          if (!response.data) {
            console.error(`Empty response received for workflow creation (${correlationId})`);
            throw new Error('Empty response received from server');
          }
          
          if (!response.data.workflow_id) {
            console.error(`Invalid response format - missing workflow_id (${correlationId}):`, response.data);
            throw new Error('Invalid response format: Missing workflow_id');
          }
          

          
          // Store the database ID in localStorage
          localStorage.setItem('db_workflow_id', response.data.workflow_id.toString());
          
          // For backwards compatibility, also store the external_id if present
          if (response.data.external_id) {
            localStorage.setItem(`workflow_${response.data.workflow_id}_external_id`, response.data.external_id);
          }
          
          // Track operation success using our helper
          this.trackApiCallSuccess(tracking, 'WorkflowCreation', clientId, response.data, {
            workflowId: String(response.data.workflow_id),
            externalId: response.data.external_id || '',
            retryCount: String(retryCount)
          });
          
          return response.data;
        } catch (error: any) {
          lastError = error;
          retryCount++;
          
          // Log detailed error info for this attempt
          console.error(`==== WORKFLOW CREATION ERROR (Attempt ${retryCount}/${maxRetries + 1}) ====`);
          console.error('Operation ID:', correlationId);
          console.error('Client ID:', clientId);
          
          if (error.response) {
            console.error('Status:', error.response.status);
            console.error('Status Text:', error.response.statusText);
            console.error('Error data:', error.response.data);
            console.error('Headers:', error.response.headers);
          } else if (error.request) {
            console.error('Request error (no response):', error.message);
            console.error('Error code:', error.code);
          } else {
            console.error('Error message:', error.message);
          }
          console.error('==================================');
          
          // Check if we should retry based on the error type
          const isRetryable = error.response?.status >= 500 || // Server errors
                             error.code === 'ECONNABORTED' ||  // Timeout
                             error.code === 'ETIMEDOUT' ||     // Timeout
                             !error.response;                  // Network errors
          
          if (retryCount <= maxRetries && isRetryable) {
            // Log retry attempt
            console.log(`Retrying workflow creation for client ${clientId} (attempt ${retryCount}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // Exponential backoff
            continue;
          }
          
          // If we're here, either we've exhausted retries or the error isn't retryable
          break;
        }
      }
      
      // If we've reached here, all retries have failed
      console.error(`All attempts failed for workflow creation (${correlationId})`);
      
      // Use our error handling helper with extra tracking properties
      return this.handleApiError('creating workflow', clientId, lastError, {
        correlationId,
        operation: 'WorkflowCreation',
        name,
        description: description || '',
        externalId: externalId || '',
        retryAttempts: String(retryCount),
        errorCode: lastError?.response?.status || lastError?.code || 'unknown'
      });
      
      // Generate a more helpful error message
      let errorMessage = 'Failed to create workflow: ';
      
      if (lastError?.response?.status) {
        errorMessage += `Server returned ${lastError.response.status} `;
        
        if (lastError.response.data?.detail) {
          errorMessage += `- ${lastError.response.data.detail}`;
        } else if (lastError.response.statusText) {
          errorMessage += `- ${lastError.response.statusText}`;
        }
      } else if (lastError?.code === 'ECONNABORTED' || lastError?.code === 'ETIMEDOUT') {
        errorMessage += 'Request timed out. The server took too long to respond.';
      } else if (lastError?.message) {
        errorMessage += lastError.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      // Create enhanced error with additional context
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).originalError = lastError;
      (enhancedError as any).correlationId = correlationId;
      (enhancedError as any).endpoint = `/api/v2/db/client/${clientId}/workflows`;
      (enhancedError as any).requestData = requestPayload;
      
      throw enhancedError;
    } catch (error) {
      return this.handleApiError('creating workflow', clientId, error);
    }
  },

  // Module 1 specific methods
  
  // Start keyword analysis using database workflow ID
  async startDbKeywordAnalysis(workflowId: string | number, inputBlob: string, isDraft: boolean, clientId?: string | number, keywordLimit?: number, customPrompt?: string): Promise<any> {
    console.log(`Starting keyword analysis for workflow ${workflowId}`);
    
    // Add telemetry tracking
    const tracking = this.trackApiCallStart('KeywordAnalysis', workflowId, {
      clientId: clientId ? String(clientId) : undefined,
      hasInputBlob: !!inputBlob,
      isDraft,
      keywordLimit,
      hasCustomPrompt: !!customPrompt
    });
    
    try {
      // More robust client ID validation - accept any non-empty value
      if (!clientId && typeof workflowId === 'string' && workflowId.includes('-')) {
        // Try to get client ID from localStorage as backup
        const localClientId = localStorage.getItem('selected_client_id');
        if (localClientId) {
          console.log(`Using client ID from localStorage: ${localClientId}`);
          clientId = localClientId;
        }
      }
      
      // Validation for input blob
      if (!inputBlob) {
        throw new Error('Missing input blob path');
      }
      
      // IMPORTANT: For a UUID-format workflow ID, we need to:
      // 1. Check if we have a cached database ID in localStorage
      // 2. If not, resolve it first via the external ID endpoint
      // 3. As a fallback, attempt to create a new workflow with the external ID
      
      // Build request with all parameters
      const requestBody: any = {
        input_blob: inputBlob.trim(),
        is_draft: isDraft
      };
      
      // Add optional parameters if provided
      if (keywordLimit !== undefined) {
        requestBody.keyword_limit = keywordLimit;
      }
      
      if (customPrompt) {
        requestBody.custom_prompt = customPrompt;
      }
      
      // Special case: UUID format workflow ID needs special handling
      if (typeof workflowId === 'string' && workflowId.includes('-')) {
        console.log(`UUID format workflowId detected: ${workflowId}`);
        
        // Try cached mapping from localStorage first
        const cachedDbId = localStorage.getItem(`external_id_${workflowId}`);
        if (cachedDbId && cachedDbId !== '-1' && /^\d+$/.test(cachedDbId)) {
          console.log(`Using cached database ID: ${cachedDbId}`);
          
          // Directly use the database ID in the endpoint
          try {
            const response = await snowflakeApi.post(
              `/api/v1/db/workflow/${cachedDbId}/analyze`,
              requestBody
            );
            return response.data;
          } catch (cachedIdError) {
            console.error(`Error using cached database ID ${cachedDbId}:`, cachedIdError);
            // Continue to try getting a new mapping - don't throw yet
          }
        }
        
        // No valid cached ID, try to resolve the external ID to a database ID
        console.log(`Resolving external ID ${workflowId} to database ID`);
        
        try {
          // First try to get workflow information by external ID
          const externalWorkflowResponse = await snowflakeApi.get(`/api/v1/db/workflow/external/${workflowId}`);
          
          if (externalWorkflowResponse.data && externalWorkflowResponse.data.workflow_id) {
            const resolvedDbId = externalWorkflowResponse.data.workflow_id;
            console.log(`Resolved workflow external ID ${workflowId} to database ID ${resolvedDbId}`);
            
            // Save this mapping for future use
            localStorage.setItem(`external_id_${workflowId}`, String(resolvedDbId));
            
            // Now make the analyze call with the numeric database ID
            const analyzeResponse = await snowflakeApi.post(
              `/api/v1/db/workflow/${resolvedDbId}/analyze`,
              requestBody
            );
            
            return analyzeResponse.data;
          }
        } catch (resolveError) {
          console.error(`Error resolving external ID ${workflowId}:`, resolveError);
          // We'll try creation if we have a client ID, otherwise throw
        }
        
        // If we're here, we need to create a new workflow
        if (!clientId) {
          console.error('No client ID provided to create workflow');
          throw new Error('Failed to resolve external ID to database ID and no client ID provided to create new workflow');
        }
        
        // Attempt to create a new workflow with the external ID
        console.log(`Creating new workflow with client ID ${clientId} and external ID ${workflowId}`);
        
        try {
          // Create a new workflow with the external ID
          let createResponse;
          
          if (typeof clientId === 'number' || /^\d+$/.test(String(clientId))) {
            // Numeric client ID
            createResponse = await snowflakeApi.post(`/api/v1/db/client/${clientId}/workflow`, {
              name: 'Auto-created workflow',
              description: 'Created during analysis',
              external_id: workflowId
            });
          } else {
            // Client code - string ID
            createResponse = await snowflakeApi.post(`/api/v1/db/client/code/${clientId}/workflow`, {
              name: 'Auto-created workflow',
              description: 'Created during analysis',
              external_id: workflowId
            });
          }
          
          if (createResponse.data && createResponse.data.workflow_id) {
            const newDbId = createResponse.data.workflow_id;
            console.log(`Created new workflow with database ID ${newDbId} for external ID ${workflowId}`);
            
            // Save this mapping for future use
            localStorage.setItem(`external_id_${workflowId}`, String(newDbId));
            
            // Now make the analyze call with the numeric database ID
            const analyzeResponse = await snowflakeApi.post(
              `/api/v1/db/workflow/${newDbId}/analyze`,
              requestBody
            );
            
            return analyzeResponse.data;
          } else {
            throw new Error('Invalid response from workflow creation');
          }
        } catch (createError) {
          console.error(`Error creating workflow:`, createError);
          throw new Error(`Cannot create workflow: ${createError instanceof Error ? createError.message : String(createError)}`);
        }
      } 
      // For numeric IDs, use the standard approach
      else {
        console.log(`Using direct database ID (non-UUID): ${workflowId}`);
        // Convert to string for URL path formatting
        const response = await snowflakeApi.post(
          `/api/v1/db/workflow/${String(workflowId)}/analyze`,
          requestBody
        );
        return response.data;
      }
    } catch (error) {
      // Track success for any successful operations that occurred before the error
      if (tracking) {
        this.trackApiCallSuccess(tracking, 'KeywordAnalysis', workflowId, null, {
          status: 'error',
          clientId: clientId ? String(clientId) : undefined
        });
      }
      
      return this.handleApiError('starting keyword analysis', workflowId, error, {
        clientId: clientId ? String(clientId) : undefined,
        operation: 'KeywordAnalysis',
        inputBlob: inputBlob ? true : false,
        isDraft,
        keywordLimit,
        customPrompt
      });
    }
  },

  // Get analysis status by workflow ID
  async getDbKeywordAnalysisStatus(workflowId: number, clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error getting analysis status for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Get analysis results by workflow ID
  async getDbKeywordAnalysisResults(workflowId: number, clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/results`);
      return response.data;
    } catch (error) {
      console.error(`Error getting analysis results for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Get analysis themes by workflow ID
  async getDbKeywordAnalysisThemes(workflowId: number, clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/themes`);
      return response.data;
    } catch (error) {
      console.error(`Error getting themes for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Add custom themes to a workflow
  async addDbCustomThemes(workflowId: number, themes: Record<string, string[]>, keywords?: string[], clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/themes`, {
        themes,
        keywords
      });
      return response.data;
    } catch (error) {
      console.error(`Error adding custom themes for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Verify analysis results for a workflow
  async verifyDbAnalysis(workflowId: number, action: 'approve' | 'reject', feedback?: string, clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/verify`, {
        action,
        feedback
      });
      return response.data;
    } catch (error) {
      console.error(`Error verifying analysis for workflow ${workflowId}:`, error);
      throw error;
    }
  },
  
  // Get analysis status by module ID
  async getDbModuleAnalysisStatus(moduleId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error getting analysis status for module ${moduleId}:`, error);
      throw error;
    }
  },

  // Module 2 specific methods
  
  // Generate code using database workflow ID
  async generateDbCode(
    workflowId: number, 
    stemWordsBlob: string, 
    sampleKeywords?: string[]
  ): Promise<any> {
    try {
      const body = sampleKeywords ? { sample_keywords: sampleKeywords } : undefined;
      const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/generate`, body, {
        params: {
          stem_words_blob: stemWordsBlob
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error generating code for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Get code generation status by workflow ID
  async getDbCodeGenerationStatus(workflowId: number, clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error getting code generation status for workflow ${workflowId}:`, error);
      throw error;
    }
  },
  
  // Get code generation status by module ID (maintained for backward compatibility)
  async getDbCodeGenerationStatusByModule(moduleId: number): Promise<ModuleResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/status`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting code generation status', moduleId, error);
    }
  },

  // Get code by workflow ID and module ID
  async getDbCode(workflowId: number, moduleId: number, clientId?: number): Promise<any> {
    try {
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/modules/${moduleId}/code`);
      return response.data;
    } catch (error) {
      console.error(`Error getting code for workflow ${workflowId}, module ${moduleId}:`, error);
      throw error;
    }
  },
  
  // Get code by module ID (maintained for backward compatibility)
  async getDbCodeByModule(moduleId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/code`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting code', moduleId, error);
    }
  },

  // Execute code using workflow ID and module ID
  async executeDbCode(workflowId: number, moduleId: number, keywordsBlob: string, clientId?: number): Promise<any> {
    try {
      const tracking = this.trackApiCallStart('ExecuteCode', workflowId, { 
        moduleId, 
        clientId,
        hasKeywordsBlob: !!keywordsBlob
      });
      
      // Keep keywords_blob parameter but remove client_id
      const params: Record<string, any> = {
        keywords_blob: keywordsBlob
      };
      
      const response = await snowflakeApi.post(
        `/api/v1/db/workflow/${workflowId}/modules/${moduleId}/execute`, 
        null, 
        { params }
      );
      
      this.trackApiCallSuccess(tracking, 'ExecuteCode', workflowId, response.data, { 
        moduleId, 
        clientId 
      });
      
      return response.data;
    } catch (error) {
      return this.handleApiError('executing code', workflowId, error, {
        moduleId,
        clientId,
        operation: 'ExecuteCode'
      });
    }
  },
  
  // Execute code using module ID (maintained for backward compatibility)
  async executeDbCodeByModule(moduleId: number, keywordsBlob: string): Promise<any> {
    try {
      const response = await snowflakeApi.post(`/api/v1/db/module/${moduleId}/execute`, null, {
        params: {
          keywords_blob: keywordsBlob
        }
      });
      return response.data;
    } catch (error) {
      return this.handleApiError('executing code', moduleId, error);
    }
  },

  // Get execution status by workflow ID
  async getDbExecutionStatus(workflowId: number, executionModuleId?: number, clientId?: number): Promise<any> {
    try {
      const additionalProps: Record<string, any> = { clientId };
      if (executionModuleId !== undefined) {
        additionalProps.executionModuleId = executionModuleId;
      }
      
      const tracking = this.trackApiCallStart('ExecutionStatus', workflowId, additionalProps);
      
      // Keep execution_module_id parameter if provided, but remove client_id
      const params: Record<string, any> = {};
      if (executionModuleId !== undefined) {
        params.execution_module_id = executionModuleId;
      }
      
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/executions/status`, {
        params
      });
      
      this.trackApiCallSuccess(tracking, 'ExecutionStatus', workflowId, response.data, additionalProps);
      
      return response.data;
    } catch (error) {
      return this.handleApiError('getting execution status', workflowId, error, {
        executionModuleId,
        clientId,
        operation: 'ExecutionStatus'
      });
    }
  },
  
  // Get execution status by module ID (maintained for backward compatibility)
  async getDbExecutionStatusByModule(moduleId: number): Promise<ModuleResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/execution/status`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting execution status', moduleId, error);
    }
  },

  // Module 3 specific methods
  
  // Start semantic analysis using database workflow ID
  async startDbSemanticAnalysis(
    workflowId: number,
    relevantKeywordsBlob: string,
    topicFrameworkBlob: string,
    forceReanalysis: boolean = false,
    clientId?: number
  ): Promise<any> {
    try {
      const tracking = this.trackApiCallStart('SemanticAnalysis', workflowId, { 
        clientId,
        hasRelevantKeywords: !!relevantKeywordsBlob,
        hasTopicFramework: !!topicFrameworkBlob,
        forceReanalysis
      });
      
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/semantic`, {
        relevant_keywords_blob: relevantKeywordsBlob,
        topic_framework_blob: topicFrameworkBlob,
        force_reanalysis: forceReanalysis
      });
      
      this.trackApiCallSuccess(tracking, 'SemanticAnalysis', workflowId, response.data, { clientId });
      
      return response.data;
    } catch (error) {
      return this.handleApiError('starting semantic analysis', workflowId, error, {
        clientId,
        operation: 'SemanticAnalysis'
      });
    }
  },

  // Get semantic analysis by workflow ID
  async getDbSemanticAnalysisByWorkflow(workflowId: number, clientId?: number): Promise<any> {
    try {
      const tracking = this.trackApiCallStart('SemanticStatusByWorkflow', workflowId, { clientId });
      
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/semantic`);
      
      this.trackApiCallSuccess(tracking, 'SemanticStatusByWorkflow', workflowId, response.data, { clientId });
      
      return response.data;
    } catch (error) {
      return this.handleApiError('getting semantic analysis', workflowId, error, { 
        clientId,
        operation: 'SemanticStatusByWorkflow'
      });
    }
  },

  // Get semantic analysis status by module ID (maintained for backward compatibility)
  async getDbSemanticAnalysisStatus(moduleId: number): Promise<ModuleResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/semantic`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting semantic analysis status', moduleId, error);
    }
  },

  // Get framework by workflow ID
  async getDbFrameworkByWorkflow(workflowId: number, clientId?: number): Promise<any> {
    try {
      const tracking = this.trackApiCallStart('FrameworkByWorkflow', workflowId, { clientId });
      
      // DB endpoints don't need client_id parameter
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/framework`);
      
      // Add frameworkId to the tracking properties
      const additionalProps = { 
        clientId,
        frameworkId: response.data?.framework_id 
      };
      
      this.trackApiCallSuccess(tracking, 'FrameworkByWorkflow', workflowId, response.data, additionalProps);
      
      return response.data;
    } catch (error) {
      return this.handleApiError('getting framework', workflowId, error, { 
        clientId,
        operation: 'FrameworkByWorkflow'
      });
    }
  },

  // Get framework by module ID (maintained for backward compatibility)
  async getDbFrameworkByModule(moduleId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/framework`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting framework', moduleId, error);
    }
  },

  // Get framework by database ID
  async getDbFrameworkById(frameworkId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/framework/${frameworkId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting framework ${frameworkId}:`, error);
      throw error;
    }
  },

  // Save framework for a module (maintained for backward compatibility)
  async saveDbFramework(moduleId: number, framework: any): Promise<any> {
    try {
      const response = await snowflakeApi.post(`/api/v1/db/module/${moduleId}/framework`, framework);
      return response.data;
    } catch (error) {
      return this.handleApiError('saving framework', moduleId, error);
    }
  },

  // Update framework
  async updateDbFramework(frameworkId: number, updates: any, trackChanges: boolean = true, userId?: number): Promise<any> {
    try {
      const response = await snowflakeApi.put(`/api/v1/db/framework/${frameworkId}`, {
        updates,
        track_changes: trackChanges,
        user_id: userId
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating framework ${frameworkId}:`, error);
      throw error;
    }
  },

  // Module 4 specific methods
  
  // Start batch processing using database workflow ID
  async startDbBatchProcess(
    workflowId: number,
    inputFile: string,
    configId?: number,
    batchSize?: number,
    priority?: number
  ): Promise<any> {
    try {
      // Prepare query parameters
      const params: Record<string, any> = {
        input_file: inputFile
      };
      
      if (configId) params.config_id = configId;
      if (batchSize) params.batch_size = batchSize;
      if (priority) params.priority = priority;
      
      const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/process`, null, { params });
      return response.data;
    } catch (error) {
      console.error(`Error starting batch process for workflow ${workflowId}:`, error);
      throw error;
    }
  },

  // Get batch process status by module ID
  async getDbBatchStatus(moduleId: number): Promise<ModuleResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/status`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting batch status', moduleId, error);
    }
  },

  // Get batch results by module ID
  async getDbBatchResults(moduleId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/results`);
      return response.data;
    } catch (error) {
      return this.handleApiError('getting batch results', moduleId, error);
    }
  },

  // Cancel batch process by module ID
  async cancelDbBatch(moduleId: number): Promise<any> {
    try {
      const response = await snowflakeApi.post(`/api/v1/db/module/${moduleId}/cancel`);
      return response.data;
    } catch (error) {
      return this.handleApiError('cancelling batch', moduleId, error);
    }
  },

  // Get configuration by database ID
  async getDbConfig(configId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/config/${configId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting config ${configId}:`, error);
      throw error;
    }
  },

  // List configurations for a client
  async getDbClientConfigs(clientId: number): Promise<any> {
    try {
      const response = await snowflakeApi.get(`/api/v1/db/client/${clientId}/configs`);
      return response.data.configs;
    } catch (error) {
      console.error(`Error getting configs for client ${clientId}:`, error);
      throw error;
    }
  },

  // Create a new configuration
  async createDbConfig(
    clientId: number,
    name: string,
    description: string,
    config: any
  ): Promise<any> {
    try {
      const response = await snowflakeApi.post('/api/v1/db/config', {
        client_id: clientId,
        name,
        description,
        ...config
      });
      return response.data;
    } catch (error) {
      console.error('Error creating config:', error);
      throw error;
    }
  },

  // Update a configuration
  async updateDbConfig(configId: number, updates: any): Promise<any> {
    try {
      const response = await snowflakeApi.put(`/api/v1/db/config/${configId}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating config ${configId}:`, error);
      throw error;
    }
  },

  // Delete a configuration
  async deleteDbConfig(configId: number): Promise<any> {
    try {
      const response = await snowflakeApi.delete(`/api/v1/db/config/${configId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting config ${configId}:`, error);
      throw error;
    }
  },

  /**
   * Sync clients from Snowflake to SQL database
   */
  async syncClientsFromSnowflake(): Promise<{ message: string; sync_stats?: { total: number; inserted: number; skipped: number } }> {
    try {
      debugLog('workflowService', 'Syncing clients from Snowflake to SQL');
      const response = await snowflakeApi.post('/api/v1/db/clients/sync');
      debugLog('workflowService', `Sync complete: ${response.data.sync_stats?.inserted || 0} new clients`);
      return response.data;
    } catch (error) {
      console.error('Error syncing clients from Snowflake:', error);
      throw error;
    }
  },

  // HELPER METHODS FOR VERSION AWARENESS AND TRANSITIONS
  
  /**
   * Convert string ID to number if possible
   */
  parseId: (id: string | number | undefined): number | undefined => {
    if (id === undefined) return undefined;
    if (typeof id === 'number') return id;
    
    // Convert string to number if possible
    if (typeof id === 'string' && /^\d+$/.test(id)) {
      const numericId = parseInt(id, 10);
      return isNaN(numericId) ? undefined : numericId;
    }
    
    return undefined;
  },
};

export default workflowService;
